/**
 * Scroll Back Up Button
 *
 * @module
 */

import { throttle } from 'lodash-es';

export class ScrollUpButton {
    /**
     * Arrow up button which sends the user to the top of the page, and only appears
     * when the user scrolls up from below the fold.
     *
     * @param {HTMLElement} button
     */
    constructor(button) {
        this.button = button;
    }

    /**
     * Initialize the reveal and the scroll on click features.
     *
     * @return {void}
     */
    init() {
        if (! this.button) {
            return;
        }
        this.initReveal();
        this.initClick();
    }

    /**
     * Initialize the reveal feature.
     *
     * @private
     * @return {void}
     */
    initReveal() {
        this.scrollUpBottom = 0;
        this.scrollHeight = window.innerHeight;
        this.scrolledFromClick = false;
        window.addEventListener('scroll', throttle(() => this.onScroll(), 100));
    }

    /**
     * Find in which direction the user is scrolling, and call the appropiate method.
     *
     * @private
     * @return {void}
     */
    onScroll() {
        const scrollY = window.scrollY;
        if (scrollY > this.scrollY) {
            this.onScrollDown(scrollY);
        } else {
            this.onScrollUp(scrollY);
        }
        this.scrollY = scrollY;
    }

    /**
     * Hide the button when the user scrolls down.
     *
     * @private
     * @return {void}
     */
    onScrollDown() {
        this.button.classList.remove('visible');
        this.scrollUpBottom = 0;
        this.scrolledFromClick = false;
    }

    /**
     * Show the button if the user scrolled up from below the fold.
     *
     * @private
     * @return {void}
     */
    onScrollUp(scrollY) {
        if (this.scrolledFromClick) {
            return;
        }
        if (this.scrollUpBottom === 0) {
            this.scrollUpBottom = scrollY;
            return this.button.classList.remove('visible');
        }
        if (scrollY < this.scrollHeight || this.scrollUpBottom - scrollY < 100) {
            return this.button.classList.remove('visible');
        }
        this.button.classList.add('visible');
    }

    /**
     * Initialize the scroll on click feature.
     *
     * @private
     * @return {void}
     */
    initClick() {
        this.button.addEventListener('click', () => {
            this.button.classList.remove('visible');
            this.scrolledFromClick = true;
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        });
    }
}
